import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { readError } from '../lib/errors'

interface Props {
  submissionId: string
  className?: string
}

/**
 * Tiny "flag" link that lets a reader send a content report. Apple's
 * Guideline 1.2.1 requires apps with user-generated content to expose
 * a reporting surface; this is it. Reports go to public.content_reports
 * via the report_weekly_submission RPC, where the developer can review
 * and remove offending stories from the Supabase dashboard.
 *
 * Deliberately understated visually — small grey text, only meant to
 * be there for the moments it's needed.
 */
export function ReportButton({ submissionId, className }: Props) {
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handle(e: React.MouseEvent) {
    e.stopPropagation()
    if (busy || done) return
    if (!window.confirm('Report this story for review? We’ll take a look and remove it if it breaks the rules.')) return
    setBusy(true); setError(null)
    // Hand-written supabase types (database.types.ts) don't list this
    // RPC; cast to escape the typed-rpc inference. See migration
    // 20260428000000_content_moderation.sql for the function shape.
    const { error: e2 } = await (supabase.rpc as unknown as (
      fn: string, args: Record<string, unknown>,
    ) => Promise<{ error: Error | null }>)(
      'report_weekly_submission',
      { p_submission_id: submissionId },
    )
    setBusy(false)
    if (e2) { setError(readError(e2)); return }
    setDone(true)
  }

  if (error) {
    return <span className={`text-[10px] text-brand-danger ${className ?? ''}`}>{error}</span>
  }

  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy || done}
      aria-label={done ? 'Reported' : 'Report this story'}
      className={`text-[10px] text-brand-muted/60 hover:text-brand-danger
                  active:scale-95 transition-all
                  disabled:opacity-50 disabled:cursor-default
                  ${className ?? ''}`}
    >
      {done ? 'Reported' : busy ? '…' : 'Flag'}
    </button>
  )
}
